export type SalesIntakeSourceDocumentType = 'customer_po' | 'quote_request' | 'work_authorization' | 'invoice_request' | 'unknown';
export type SalesIntakeRecommendedTarget = 'quote' | 'sales_order' | 'work_order' | 'invoice';

export const SALES_INTAKE_SOURCE_LABELS: Record<SalesIntakeSourceDocumentType, string> = {
  customer_po: 'Customer PO',
  quote_request: 'Quote request',
  work_authorization: 'Work authorization',
  invoice_request: 'Invoice request',
  unknown: 'General sales document',
};

export const SALES_INTAKE_TARGET_LABELS: Record<SalesIntakeRecommendedTarget, string> = {
  quote: 'Create quote',
  sales_order: 'Create sales order',
  work_order: 'Create work order',
  invoice: 'Create invoice',
};

export interface SalesIntakePreviewLine {
  description: string;
  quantity: number | null;
  unit_price: number | null;
  amount: number | null;
}

export interface SalesIntakeLineInput {
  description?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  amount?: number | null;
  total?: number | null;
}

export interface SalesIntakePreview {
  source_document_type: SalesIntakeSourceDocumentType;
  recommended_target: SalesIntakeRecommendedTarget;
  customer_name: string | null;
  customer_po_number: string | null;
  requested_date: string | null;
  total_amount: number | null;
  currency: string | null;
  confidence: number;
  warnings: string[];
  lines: SalesIntakePreviewLine[];
  text_preview: string | null;
}

export interface BuildSalesIntakePreviewInput {
  fileName?: string | null;
  extractedText?: string | null;
  lineItems?: SalesIntakeLineInput[] | null;
  totalAmount?: number | null;
  documentDate?: string | null;
  counterpartyName?: string | null;
  currency?: string | null;
}

const CUSTOMER_NAME_LABELS = ['customer', 'client', 'bill to', 'sold to', 'company', 'account name', 'attention', 'attn'];
const FALSE_POSITIVE_NAME_TOKENS = ['purchase order', 'quote request', 'delivery note', 'sales order', 'invoice', 'quotation'];
const ORDER_REFERENCE_BLACKLIST = new Set(['VAT', 'TAX', 'TOTAL', 'DATE', 'TEL', 'FAX', 'EMAIL']);
const DATE_KEYWORDS = ['delivery date', 'deliver by', 'required by', 'requested by', 'requested date', 'service date', 'work date', 'due date'];
const DOC_TYPE_HINTS: Array<{ type: SalesIntakeSourceDocumentType; patterns: RegExp[] }> = [
  {
    type: 'quote_request',
    patterns: [/quote request/i, /request for quote/i, /\brfq\b/i, /quotation request/i],
  },
  {
    type: 'work_authorization',
    patterns: [/work authorization/i, /service authorization/i, /work order/i, /job card/i, /scope of work/i],
  },
  {
    type: 'invoice_request',
    patterns: [/invoice request/i, /tax invoice required/i, /bill us/i, /invoice me/i],
  },
  {
    type: 'customer_po',
    patterns: [/purchase order/i, /customer po/i, /customer order/i, /sales order/i],
  },
];

function normalizeLineBreaks(value: string) {
  return value.replace(/\r/g, '');
}

function normalizeWhitespace(value: string) {
  return normalizeLineBreaks(value)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

function buildPreviewText(value: string): string | null {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return null;
  }

  if (normalized.length <= 360) {
    return normalized;
  }

  return `${normalized.slice(0, 360).trim()}…`;
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function parseAmountCandidate(rawValue: string): number | null {
  const stripped = rawValue.replace(/[^0-9,.-]/g, '');
  if (!stripped) {
    return null;
  }

  const commaCount = (stripped.match(/,/g) || []).length;
  const dotCount = (stripped.match(/\./g) || []).length;

  let normalized = stripped;
  if (commaCount > 0 && dotCount === 0) {
    normalized = stripped.replace(/,/g, '.');
  } else if (commaCount > 0 && dotCount > 0) {
    normalized = stripped.replace(/,(?=\d{3}(\D|$))/g, '');
  } else {
    normalized = stripped.replace(/,/g, '');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeDateCandidate(candidate: string): string | null {
  const cleaned = candidate.replace(/[,]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) {
    return null;
  }

  const ddMmYyyy = cleaned.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (ddMmYyyy) {
    const [, day, month, year] = ddMmYyyy;
    const normalizedYear = year.length === 2 ? `20${year}` : year;
    const parsed = new Date(`${normalizedYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
  }

  const direct = new Date(cleaned);
  if (!Number.isNaN(direct.getTime())) {
    return direct.toISOString().split('T')[0];
  }

  return null;
}

function isLikelyOrderReference(candidate: string) {
  const normalized = candidate.replace(/[^A-Z0-9/-]/gi, '').toUpperCase();
  if (normalized.length < 3) {
    return false;
  }

  if (ORDER_REFERENCE_BLACKLIST.has(normalized)) {
    return false;
  }

  if (!/[0-9]/.test(normalized) && normalized.length < 6) {
    return false;
  }

  return true;
}

function extractCustomerPoNumber(text: string, fileName?: string | null): string | null {
  const haystack = `${fileName || ''}\n${text}`;
  const patterns = [
    /(?:customer\s*)?p(?:urchase)?\s*o(?:rder)?(?:\s*(?:number|no\.?|#|num))?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-/]{2,})/i,
    /\bpo(?:\s*(?:number|no\.?|#))?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-/]{2,})\b/i,
    /\border(?:\s*(?:number|no\.?|#))?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-/]{2,})\b/i,
  ];

  for (const pattern of patterns) {
    const match = haystack.match(pattern);
    const candidate = match?.[1]?.trim();
    if (candidate && isLikelyOrderReference(candidate)) {
      return candidate;
    }
  }

  return null;
}

function inferSourceDocumentType(text: string, fileName?: string | null): SalesIntakeSourceDocumentType {
  const haystack = `${fileName || ''}\n${text}`;

  for (const hint of DOC_TYPE_HINTS) {
    if (hint.patterns.some((pattern) => pattern.test(haystack))) {
      return hint.type;
    }
  }

  return 'unknown';
}

function cleanCustomerName(value: string | null | undefined): string | null {
  const cleaned = value?.replace(/\s+/g, ' ').trim() || '';
  if (!cleaned) {
    return null;
  }

  const lower = cleaned.toLowerCase();
  if (FALSE_POSITIVE_NAME_TOKENS.some((token) => lower.includes(token))) {
    return null;
  }

  if (cleaned.length < 2) {
    return null;
  }

  return cleaned;
}

function extractCustomerName(text: string, fallbackName?: string | null): string | null {
  const fallback = cleanCustomerName(fallbackName);
  if (fallback) {
    return fallback;
  }

  const lines = normalizeLineBreaks(text)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const lower = line.toLowerCase();
    const labelledMatch = line.match(/^(customer|client|bill to|sold to|company|account name|attention|attn)\s*[:\-]\s*(.+)$/i);
    if (labelledMatch) {
      const candidate = cleanCustomerName(labelledMatch[2]);
      if (candidate) {
        return candidate;
      }
    }

    if (CUSTOMER_NAME_LABELS.includes(lower) && lines[index + 1]) {
      const candidate = cleanCustomerName(lines[index + 1]);
      if (candidate) {
        return candidate;
      }
    }
  }

  return null;
}

function detectDelimiter(lines: string[]): string | null {
  const delimiters = ['\t', ',', ';', '|'];
  let bestDelimiter: string | null = null;
  let bestScore = 0;

  delimiters.forEach((delimiter) => {
    const score = lines.reduce((total, line) => total + Math.max(0, line.split(delimiter).length - 1), 0);
    if (score > bestScore) {
      bestScore = score;
      bestDelimiter = delimiter;
    }
  });

  return bestScore >= 4 ? bestDelimiter : null;
}

function findHeaderIndex(lines: string[], delimiter: string) {
  return lines.findIndex((line) => {
    const normalizedCells = line
      .split(delimiter)
      .map((cell) => cell.trim().toLowerCase());

    const hasDescription = normalizedCells.some((cell) => ['description', 'item', 'product', 'service', 'details'].includes(cell));
    const hasMetrics = normalizedCells.some((cell) => ['qty', 'quantity', 'unit price', 'price', 'rate', 'amount', 'total', 'line total'].includes(cell));

    return hasDescription && hasMetrics;
  });
}

function extractDelimitedLineItems(text: string): SalesIntakePreviewLine[] {
  const lines = normalizeLineBreaks(text)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 40);

  const delimiter = detectDelimiter(lines);
  if (!delimiter) {
    return [];
  }

  const headerIndex = findHeaderIndex(lines, delimiter);
  if (headerIndex < 0) {
    return [];
  }

  const headerCells = lines[headerIndex].split(delimiter).map((cell) => cell.trim().toLowerCase());
  const descriptionIndex = headerCells.findIndex((cell) => ['description', 'item', 'product', 'service', 'details'].includes(cell));
  const quantityIndex = headerCells.findIndex((cell) => ['qty', 'quantity'].includes(cell));
  const unitPriceIndex = headerCells.findIndex((cell) => ['unit price', 'price', 'rate'].includes(cell));
  const amountIndex = headerCells.findIndex((cell) => ['amount', 'total', 'line total', 'value'].includes(cell));

  if (descriptionIndex < 0 || (quantityIndex < 0 && unitPriceIndex < 0 && amountIndex < 0)) {
    return [];
  }

  const results: SalesIntakePreviewLine[] = [];
  for (const line of lines.slice(headerIndex + 1)) {
    const cells = line.split(delimiter).map((cell) => cell.trim());
    const description = cells[descriptionIndex] || '';
    const quantity = quantityIndex >= 0 ? parseAmountCandidate(cells[quantityIndex] || '') : null;
    const unitPrice = unitPriceIndex >= 0 ? parseAmountCandidate(cells[unitPriceIndex] || '') : null;
    const amount = amountIndex >= 0 ? parseAmountCandidate(cells[amountIndex] || '') : null;

    if (!description || (!quantity && !unitPrice && !amount)) {
      continue;
    }

    results.push({
      description,
      quantity,
      unit_price: unitPrice,
      amount,
    });

    if (results.length >= 8) {
      break;
    }
  }

  return results;
}

function normalizeProvidedLineItems(lineItems?: SalesIntakeLineInput[] | null): SalesIntakePreviewLine[] {
  if (!Array.isArray(lineItems)) {
    return [];
  }

  return lineItems
    .map((line) => ({
      description: (line.description || '').trim(),
      quantity: asFiniteNumber(line.quantity),
      unit_price: asFiniteNumber(line.unit_price),
      amount: asFiniteNumber(line.amount) ?? asFiniteNumber(line.total),
    }))
    .filter((line) => line.description || line.amount != null);
}

function extractRequestedDate(text: string, fallbackDate?: string | null): string | null {
  const normalizedText = normalizeLineBreaks(text);

  for (const keyword of DATE_KEYWORDS) {
    const regex = new RegExp(`${keyword}[^\\n\\r:]{0,12}[:\\-]?\\s*([A-Za-z]{3,9}\\s+\\d{1,2},?\\s+\\d{4}|\\d{1,2}[/-]\\d{1,2}[/-]\\d{2,4}|\\d{4}[/-]\\d{1,2}[/-]\\d{1,2}|\\d{1,2}\\s+[A-Za-z]{3,9}\\s+\\d{4})`, 'i');
    const match = normalizedText.match(regex);
    const normalizedDate = normalizeDateCandidate(match?.[1] || '');
    if (normalizedDate) {
      return normalizedDate;
    }
  }

  return fallbackDate ? normalizeDateCandidate(fallbackDate) : null;
}

function extractCurrency(text: string, fallbackCurrency?: string | null): string | null {
  const fallback = fallbackCurrency?.trim().toUpperCase();
  if (fallback) {
    return fallback;
  }

  const currencyPatterns: Array<{ pattern: RegExp; code: string }> = [
    { pattern: /\bZAR\b|R\s*\d/i, code: 'ZAR' },
    { pattern: /\bUSD\b|\$\s*\d/i, code: 'USD' },
    { pattern: /\bEUR\b|€\s*\d/i, code: 'EUR' },
    { pattern: /\bGBP\b|£\s*\d/i, code: 'GBP' },
  ];

  const match = currencyPatterns.find((entry) => entry.pattern.test(text));
  return match?.code || null;
}

function extractTotalAmount(text: string, fallbackAmount?: number | null): number | null {
  if (fallbackAmount != null && Number.isFinite(fallbackAmount)) {
    return fallbackAmount;
  }

  const labelledPatterns = [
    /(?:grand\s+total|order\s+total|document\s+total|total\s+amount|total)\s*[:\-]?\s*(?:ZAR|USD|EUR|GBP|R|\$|€|£)?\s*([0-9][0-9,\.\s]*)/i,
    /(?:amount\s+due|balance\s+due)\s*[:\-]?\s*(?:ZAR|USD|EUR|GBP|R|\$|€|£)?\s*([0-9][0-9,\.\s]*)/i,
  ];

  for (const pattern of labelledPatterns) {
    const match = text.match(pattern);
    const amount = parseAmountCandidate(match?.[1] || '');
    if (amount != null) {
      return amount;
    }
  }

  return null;
}

function getRecommendedTarget(
  sourceType: SalesIntakeSourceDocumentType,
  customerPoNumber: string | null,
): SalesIntakeRecommendedTarget {
  switch (sourceType) {
    case 'quote_request':
      return 'quote';
    case 'work_authorization':
      return 'work_order';
    case 'invoice_request':
      return 'invoice';
    case 'customer_po':
      return 'sales_order';
    default:
      return customerPoNumber ? 'sales_order' : 'quote';
  }
}

function dedupeWarnings(warnings: string[]) {
  return Array.from(new Set(warnings.filter(Boolean)));
}

function calculateConfidence(input: {
  sourceType: SalesIntakeSourceDocumentType;
  customerName: string | null;
  customerPoNumber: string | null;
  requestedDate: string | null;
  totalAmount: number | null;
  lineCount: number;
}) {
  let confidence = 0.2;

  if (input.sourceType !== 'unknown') confidence += 0.15;
  if (input.customerName) confidence += 0.2;
  if (input.customerPoNumber) confidence += 0.2;
  if (input.requestedDate) confidence += 0.1;
  if (input.totalAmount != null) confidence += 0.1;
  if (input.lineCount > 0) confidence += 0.15;

  return Math.max(0.15, Math.min(0.95, Number(confidence.toFixed(2))));
}

export function buildSalesIntakePreview(input: BuildSalesIntakePreviewInput): SalesIntakePreview {
  const extractedText = normalizeWhitespace(input.extractedText || '');
  const providedLines = normalizeProvidedLineItems(input.lineItems);
  const textLines = providedLines.length > 0 ? [] : extractDelimitedLineItems(extractedText);
  const lines = providedLines.length > 0 ? providedLines : textLines;
  const customerPoNumber = extractCustomerPoNumber(extractedText, input.fileName);
  const sourceDocumentType = inferSourceDocumentType(extractedText, input.fileName);
  const customerName = extractCustomerName(extractedText, input.counterpartyName);
  const requestedDate = extractRequestedDate(extractedText, input.documentDate);
  const totalAmount = extractTotalAmount(extractedText, input.totalAmount);
  const currency = extractCurrency(extractedText, input.currency);
  const warnings = dedupeWarnings([
    !customerPoNumber ? 'No customer PO / order number was detected yet.' : '',
    !customerName ? 'Customer name could not be matched from this file.' : '',
    sourceDocumentType === 'unknown' ? 'The document type could not be classified automatically, so confirm the next sales document before conversion.' : '',
    lines.length === 0 ? 'No structured line items were extracted yet.' : '',
    !requestedDate ? 'No requested delivery / service date was detected yet.' : '',
  ]);

  return {
    source_document_type: sourceDocumentType,
    recommended_target: getRecommendedTarget(sourceDocumentType, customerPoNumber),
    customer_name: customerName,
    customer_po_number: customerPoNumber,
    requested_date: requestedDate,
    total_amount: totalAmount,
    currency,
    confidence: calculateConfidence({
      sourceType: sourceDocumentType,
      customerName,
      customerPoNumber,
      requestedDate,
      totalAmount,
      lineCount: lines.length,
    }),
    warnings,
    lines,
    text_preview: buildPreviewText(extractedText),
  };
}

export function getSalesIntakeSourceLabel(sourceType: SalesIntakeSourceDocumentType) {
  return SALES_INTAKE_SOURCE_LABELS[sourceType] || SALES_INTAKE_SOURCE_LABELS.unknown;
}

export function getSalesIntakeTargetLabel(target: SalesIntakeRecommendedTarget) {
  return SALES_INTAKE_TARGET_LABELS[target] || SALES_INTAKE_TARGET_LABELS.quote;
}
